import { isFeatureEnabled } from '@/config/preview-features';
import type { CursorPage } from '@/hooks/use-cursor-select';
import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { queryKeys } from '@/lib/api/query-keys';
import { createResource } from '@/lib/api/resource';
import type { PageMeta } from '@/types/api';

import { customerActivityLogToListQuery } from './activity-log-filters';
import { deviceAlertToListQuery } from './device-alert-filters';
import { customerDeviceToListQuery } from './device-filters';
import { toListQuery } from './filters';
import { orderHistoryToListQuery } from './order-history-filters';
import {
  associateDealerOptionPageSchema,
  customerActivityLogListResponseSchema,
  customerDetailSchema,
  customerDeviceAlertFilterOptionsSchema,
  customerDeviceAlertListResponseSchema,
  customerDeviceListResponseSchema,
  customerEditSchema,
  customerListResponseSchema,
  customerSubscriptionListResponseSchema,
  orderHistoryDealerOptionsSchema,
  orderHistoryListResponseSchema,
  zipLookupSchema,
} from './schemas';
import { customerSubscriptionToListQuery } from './subscription-filters';
import type {
  AssociateDealerOption,
  CustomerActivityLogListParams,
  CustomerActivityLogRow,
  CustomerDetail,
  CustomerDeviceAlertFilterOptions,
  CustomerDeviceAlertListParams,
  CustomerDeviceAlertRow,
  CustomerDeviceFilters,
  CustomerDeviceListParams,
  CustomerDeviceRow,
  CustomerEditDetail,
  CustomerListParams,
  CustomerListResponse,
  CustomerSubscriptionListParams,
  CustomerSubscriptionRow,
  CustomerWriteBody,
  OrderHistoryDealerOption,
  OrderHistoryFilters,
  OrderHistoryListParams,
  OrderHistoryRow,
  ZipLookup,
} from './types';

export { toListQuery };

/** Single customer detail (header). `apiClient.get` unwraps the `{ data }` envelope. */
export async function getCustomer(id: string): Promise<CustomerDetail> {
  const data = await apiClient.get(endpoints.customers.detail(id));
  return customerDetailSchema.parse(data);
}

/** Full-address customer detail for the Edit prefill (ticket 03). Same endpoint as `getCustomer`
 *  but parses the write-shaped superset — a DISTINCT concern/key from the display-only detail. */
export async function getCustomerForEdit(id: string): Promise<CustomerEditDetail> {
  const data = await apiClient.get(endpoints.customers.detail(id));
  return customerEditSchema.parse(data);
}

/** Sort + filters → the list's wire params, defaults included. The export sends the same, so the
 *  file comes out with the rows and order on screen, sorted or not (ADO 120974). */
export function customersRequestQuery(params: Omit<CustomerListParams, 'page' | 'limit'>) {
  const { sortBy, sortOrder, ...filters } = params;
  return {
    sortBy,
    sortOrder,
    ...toListQuery(filters),
    // The BE lists Dealers unless told not to; Release 1 must not show them (grilling Q13).
    ...(isFeatureEnabled('dealers') ? {} : { accountType: 'customer' }),
  };
}

/** Paginated / sorted / filtered / searchable customer list. Backend default order when
 *  `sortBy` is unset (no explicit default sort — AC). */
export async function listCustomers(params: CustomerListParams): Promise<CustomerListResponse> {
  const { page, limit, ...rest } = params;
  const { data, meta } = await apiClient.getList(endpoints.customers.list, {
    params: { page, limit, ...customersRequestQuery(rest) },
  });
  return customerListResponseSchema.parse({ data, meta });
}

/** Activity Log list for one customer (ticket 08). Returns the raw `{ data, meta }` the DataTable
 *  request bridge (`makeListRequest`) expects. */
export async function listCustomerActivityLog(
  id: string,
  params: CustomerActivityLogListParams,
): Promise<{ data: CustomerActivityLogRow[]; meta: PageMeta }> {
  const { page, limit, sortBy, sortOrder, ...filters } = params;
  const { data, meta } = await apiClient.getList<CustomerActivityLogRow>(
    endpoints.customers.activityLog(id),
    { params: { page, limit, sortBy, sortOrder, ...customerActivityLogToListQuery(filters) } },
  );
  return customerActivityLogListResponseSchema.parse({ data, meta });
}

/** Subscription-period list for one customer (ticket 06), filter-serializing (ticket 07). Returns
 *  the raw `{ data, meta }` the DataTable request bridge (`makeListRequest`) expects. */
export async function listCustomerSubscriptions(
  id: string,
  params: CustomerSubscriptionListParams,
): Promise<{ data: CustomerSubscriptionRow[]; meta: PageMeta }> {
  const { page, limit, sortBy, sortOrder, ...filters } = params;
  const { data, meta } = await apiClient.getList<CustomerSubscriptionRow>(
    endpoints.customers.subscriptions(id),
    { params: { page, limit, sortBy, sortOrder, ...customerSubscriptionToListQuery(filters) } },
  );
  return customerSubscriptionListResponseSchema.parse({ data, meta });
}

/** The stored invoice PDF for a subscription row (117768/117774) — the same file the Customer
 *  receives, keyed on `invoiceId` alone (one route serves order and subscription invoices). */
export function getInvoiceFile(invoiceId: string) {
  return apiClient.getFile(endpoints.invoices.download(invoiceId));
}

/** Devices list for one customer (ticket 04) — subscription-bearing only (backend-filtered).
 *  Returns the raw `{ data, meta }` the DataTable request bridge (`makeListRequest`) expects. */
export async function listCustomerDevices(
  id: string,
  params: CustomerDeviceListParams & CustomerDeviceFilters,
): Promise<{ data: CustomerDeviceRow[]; meta: PageMeta }> {
  const { page, limit, sortBy, sortOrder, ...filters } = params;
  const { data, meta } = await apiClient.getList<CustomerDeviceRow>(
    endpoints.customers.devices(id),
    { params: { page, limit, sortBy, sortOrder, ...customerDeviceToListQuery(filters) } },
  );
  return customerDeviceListResponseSchema.parse({ data, meta });
}

/** Order History list for one customer. Serializes the drawer filters to query params
 *  (mirrors `listOrders`); the DataTable request bridge (`makeListRequest`) consumes the
 *  returned `{ data, meta }`. */
export async function listCustomerOrders(
  id: string,
  params: OrderHistoryListParams & OrderHistoryFilters,
): Promise<{ data: OrderHistoryRow[]; meta: PageMeta }> {
  const { page, limit, sortBy, sortOrder, ...filters } = params;
  const { data, meta } = await apiClient.getList<OrderHistoryRow>(endpoints.customers.orders(id), {
    params: { page, limit, sortBy, sortOrder, ...orderHistoryToListQuery(filters) },
  });
  return orderHistoryListResponseSchema.parse({ data, meta });
}

/** Customer write resource (create/update/delete) via the shared factory. Void writes (no
 *  `writeSchema`): success invalidates `['customers']`, the listing key, which refetches.
 *  `detailSchema`/`listResponseSchema`/`toQuery` satisfy the factory config but its `useDetail`
 *  and `listFn` are NOT exposed — the read path stays hand-written (`getCustomer`/`listCustomers`).
 *  Status is hand-written below (the factory PATCHes `/:id`, not the contract's `/:id/status`). */
export const customersResource = createResource<CustomerWriteBody, CustomerWriteBody>()({
  endpoint: endpoints.customers.list,
  keyPrefix: 'customers',
  detailSchema: customerDetailSchema,
  listResponseSchema: customerListResponseSchema,
  toQuery: toListQuery,
  // A Customer's Associate Dealer drives that Dealer's customer and Sold-device counts.
  alsoInvalidates: [queryKeys.dealers.all],
});

/** PATCH /customers/admin/:id/status — the contract's status sub-path (mirrors discounts). */
export async function setCustomerStatus(id: string, status: 'active' | 'inactive'): Promise<void> {
  await apiClient.patch(endpoints.customers.status(id), { status });
}

/** ZIP → { state, city, cities[] } lookup for the address form's State/City auto-population
 *  (ticket 113129). `apiClient.get` unwraps the `{ data }` envelope; an unknown ZIP surfaces as a
 *  422 `INVALID_ZIPCODE` `ApiError`. */
export async function lookupZip(zipcode: string): Promise<ZipLookup> {
  const data = await apiClient.get(endpoints.customers.zipLookup(zipcode));
  return zipLookupSchema.parse(data);
}

/** Device Alerts sort as the API takes it (118646). Unsorted => the AC default, Log Date & Time
 *  newest first, sent explicitly rather than left to the server's own default. Shared by the list
 *  and the export, so the file is in exactly the order on screen (ADO 120974). */
export function deviceAlertSortQuery({
  sortBy = 'loggedAt',
  sortOrder = 'desc',
}: Pick<CustomerDeviceAlertListParams, 'sortBy' | 'sortOrder'>) {
  return { sortBy, sortOrder: sortOrder.toUpperCase() };
}

/** Device Alerts for one customer (118646). */
export async function listCustomerDeviceAlerts(
  id: string,
  { page, limit, sortBy, sortOrder, ...filters }: CustomerDeviceAlertListParams,
): Promise<{ data: CustomerDeviceAlertRow[]; meta: PageMeta }> {
  const { data, meta } = await apiClient.getList<CustomerDeviceAlertRow>(
    endpoints.customers.deviceAlerts(id),
    {
      params: {
        page,
        limit,
        ...deviceAlertSortQuery({ sortBy, sortOrder }),
        ...deviceAlertToListQuery(filters),
      },
    },
  );
  return customerDeviceAlertListResponseSchema.parse({ data, meta });
}

/** Device Alerts filter options (118646): this Customer's alerting Devices and their categories. */
export async function getCustomerDeviceAlertFilterOptions(
  id: string,
): Promise<CustomerDeviceAlertFilterOptions> {
  const data = await apiClient.get(endpoints.customers.deviceAlertsFilters(id));
  return customerDeviceAlertFilterOptionsSchema.parse(data);
}

/** Order History Dealer filter options (119672). */
export async function getCustomerOrderDealerOptions(
  id: string,
): Promise<OrderHistoryDealerOption[]> {
  const data = await apiClient.get(endpoints.customers.ordersDealers(id));
  return orderHistoryDealerOptionsSchema.parse(data);
}

/** Associate Dealer filter options (119030): only Dealers with ≥1 Customer, "-" first. */
export async function listAssociateDealerOptions({
  search,
  limit,
  cursor,
}: {
  search: string;
  limit: number;
  cursor?: string;
}): Promise<CursorPage<AssociateDealerOption>> {
  const page = await apiClient.getList(endpoints.customers.associateDealers, {
    params: { search: search || undefined, limit, cursor },
  });
  const { data, meta } = associateDealerOptionPageSchema.parse(page);
  return { data, hasNextPage: meta.hasNextPage, nextCursor: meta.nextCursor ?? undefined };
}
